import { useCallback, useEffect, useMemo } from 'react';
// oxlint-disable-next-line no-restricted-imports
import { useParams } from 'react-router';
import { useStorageSlot } from '@gonfalon/browser-storage/useStorageSlot';
import { merge } from 'lodash';

import type { StorageType } from '../utils';

export const defaultValues: StorageType = {
  // flags
  canInviteTeammates: false,
  detectSdkActive: true,
  enableAiConfigs: false,
  enableObservability: false,
  enableCursorLinks: true,
  isAuthenticated: true,
  // account context
  memberId: 'your-member-id',
  projectEnvironmentsPath: '/projects/default/settings/environments',
  sdkInitKeys: {
    clientSide: 'your-client-side-id',
    mobile: 'your-mobile-id',
    serverSide: 'your-server-side-id',
  },
  selectedProject: null,
  selectedEnvironment: null,
  isLoadingSdkKeys: false,

  // user selections
  product: 'flags',
  sdk: 'react-client-sdk',
};

/**
 * Hook to store initial dogfood flags, account values and user inputs to
 * session storage. Tracking functions are saved to the context api.
 * See reactContext/OnboardingContext.
 *
 * @param initialValues - dogfood flags and account values to initialize the storage with
 * @returns onboarding storage values and a setter function
 */
export const useOnboardingStorage = (initialValues: Partial<StorageType> = {}) => {
  const { onboardingPath: product } = useParams<{ onboardingPath: string }>();
  const [slotValue, slot] = useStorageSlot('sdk-setup', { storageType: 'sessionStorage' });

  // Memoize def to prevent recreating on every render
  const def = useMemo(() => {
    return slotValue ? (JSON.parse(slotValue) as StorageType) : merge({}, defaultValues, { product });
  }, [slotValue, product]);

  useEffect(() => {
    if (Object.keys(initialValues).length > 0) {
      slot.set(JSON.stringify(merge({}, def, initialValues)));
    }
  }, []);

  // setOnboardingStorage reads from storage directly to avoid depending on def
  // This ensures we always merge with the current storage value, not a stale def
  const setOnboardingStorage = useCallback(
    (value: Partial<StorageType>) => {
      const currentValue = slot.get();
      const currentStorage = currentValue
        ? (JSON.parse(currentValue) as StorageType)
        : merge({}, defaultValues, { product });
      slot.set(JSON.stringify(merge({}, currentStorage, value)));
    },
    [slot, product],
  );

  const resetOnboardingStorage = useCallback(() => {
    slot.set(JSON.stringify(merge({}, defaultValues, { product })));
  }, [slot, product]);

  return { ...def, setOnboardingStorage, resetOnboardingStorage };
};
